import { DialogProps, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUser } from 'src/components/hooks/useUser';
import 'src/components/StartUp/ipManagement/IpManagement.scss';
import { sendRequest } from 'src/hooks/useFetch';
import { store } from 'src/store';
import { setConfirmExitValue } from 'src/store/confirmExitValue';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setMessage } from 'src/store/Message';
import { PopupComponent } from '../common/PopupComponent';
import { useFieldTranslation } from '../fields/useFieldTranslation';
import { useDocumentUploads } from '../StartUp/documentUpload/useDocumentUploads';
import SelectSubartifact from './SelectSubartifact';
import { hideLoader, showLoader } from 'src/store/Loader';
import Loader from '../common/Loader';

export interface ISubartifactVersions {
  document_id: string;
  tmf_document_id: string;
  tmf_document_name: string;
  tmf_doc_version_number: number;
  tmf_doc_type: string;
  tmf_doc_subtype: string;
  tmf_artifact_number: string;
  tmf_artifact_name: string;
  tmf_doc_level: string;
  tmf_doc_study: string;
  tmf_doc_country: string;
  tmf_doc_site: string;
  tmf_doc_status: string;
}

export default function VersionControl({
  metaData,
  handleCloseVersion,
  handleBackVersion,
  setSubmitDisable,
  resData = {},
  selectData,
  ipReleaseData = {},
  createIPRelease = undefined,
  artifactName,
  getUploadedDocuments,
  createDrugSupplyDoc = undefined,
  selectedRecordId = '',
  auditmodule = Object.keys(ipReleaseData).length
    ? 'IP MANAGEMENT'
    : createDrugSupplyDoc
    ? 'CONDUCT'
    : 'START UP PACKET'
}: any) {
  const [versionNumber, setVersionNumber] = useState(``);
  const dispatch = useDispatch();
  const dispatchApp = useAppDispatch();
  const { user } = useUser();
  const [openSubartifact, setOpenSubartifact] = useState<boolean>(false);
  const [subartifactVersions, setSubartifactVersions] = useState<ISubartifactVersions[]>([]);
  const { ipmFileuploadErrorMsg } = useFieldTranslation();
  const { createMasterDocumentEtmf, updateInlineMasteDocEtmfQuerry } = useDocumentUploads();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const [isChanged, setIsChanged] = useState(false);
  const handleVersionChange = (e: any) => {
    setIsChanged(true);
    const value = e.target.value;
    setVersionNumber(value);
  };
  const dispatchMessageOnDocUpdate = (success: boolean, messageType: string) => {
    success
      ? dispatchApp(setMessage({ msgDetails: `Document ${messageType} successfully.`, severity: 'success' }))
      : dispatchApp(setMessage({ msgDetails: `Error in ${messageType} document.`, severity: 'error' }));
  };

  const createDrugSupplyDocument = async (documentId: string, tmfDocId: string) => {
    await createDrugSupplyDoc({
      data: { filemaster_id: documentId, tmf_document_id: tmfDocId },
      drug_supply_id: selectedRecordId,
      study_id: studyId,
      application: process.env.REACT_APP_NAME,
      application_section: process.env.REACT_APP_CONDUCT_NAME,
      application_module_section: process.env.REACT_APP_CONDUCT_NAME
    });
  };
  const getAllSubartifacts = async () => {
    dispatch(showLoader());
    try {
      const options = {
        endPointEnv: 'etmf',
        pathVariables: {
          appName: process.env.REACT_APP_NAME,
          tmfDocLevel: selectData.document_data.tmf_doc_level,
          tmfDocStudy: selectData.document_data.tmf_doc_study,
          tmfArtifactName: selectData.document_data.tmf_artifact_name,
          module_section_related: auditmodule,
          parent_entity_related: ''
        }
      };
      const result: any = await sendRequest('get_subartifact_versions', options);
      setSubartifactVersions(result);
      setOpenSubartifact(true);
    } catch (error: any) {
      dispatchApp(setMessage({ msgDetails: `Error in getting subartifact versions.`, severity: 'error' }));
    }
    dispatch(hideLoader());
  };

  const overwriteVersionData = async (overwriteDocData: any) => {
    dispatch(showLoader());
    try {
      const updateResponse: any = await updateInlineMasteDocEtmfQuerry({
        data: overwriteDocData,
        study_id: studyId,
        application: process.env.REACT_APP_NAME,
        module_section_related: auditmodule,
        parent_entity_related: '',
        ...(overwriteDocData.document_data.tmf_doc_level === 'Country' && { country: selectedCountry }),
        ...(overwriteDocData.document_data.tmf_doc_level === 'Site' && { site_id: selectedSite })
      });
      if (updateResponse?.data) {
        if (createDrugSupplyDoc) {
          await createDrugSupplyDocument(
            overwriteDocData.document_data.id,
            overwriteDocData.document_data.tmf_document_id
          );
        }
      }
      setOpenSubartifact(false);
      handleCloseVersion();
      dispatchMessageOnDocUpdate(true, 'updated');
    } catch (error) {
      dispatchMessageOnDocUpdate(false, 'updating');
    }
    dispatch(hideLoader());
  };

  const addSubartifactData = async (subartifactDocData: any) => {
    dispatch(showLoader());
    try {
      const result: any = await createMasterDocumentEtmf({
        data: subartifactDocData,
        study_id: studyId,
        application: process.env.REACT_APP_NAME,
        module_section_related: auditmodule,
        parent_entity_related: '',
        ...(subartifactDocData.document_data.tmf_doc_level === 'Country' && { country: selectedCountry }),
        ...(subartifactDocData.document_data.tmf_doc_level === 'Site' && { site_id: selectedSite })
      });
      const response = result?.data;
      if (ipReleaseData && createIPRelease) {
        const isNewDocExistsInIPReleaseData = ipReleaseData.some(
          (releaseDoc: any) => releaseDoc.ip_management_number === response.document_data.tmf_artifact_number
        );
        if (!isNewDocExistsInIPReleaseData) {
          const mockObj = {
            artifact_tooltip: '',
            ip_management_document: response.document_data.tmf_artifact_name,
            ip_management_number: response.document_data.tmf_artifact_number,
            ip_management_comment: ''
          };
          const modifiedObj = [];
          modifiedObj.push(mockObj);
          createIPRelease(modifiedObj);
          setConfirmExitValue(dispatch, true);
        }
      } else if (createDrugSupplyDoc) {
        await createDrugSupplyDocument(response?.document_data.id, response?.document_data.tmf_document_id);
      } else {
        getUploadedDocuments(artifactName);
      }
      dispatchMessageOnDocUpdate(true, 'uploaded');
    } catch (error: any) {
      dispatchMessageOnDocUpdate(false, 'uploading');
    }
    dispatch(hideLoader());
  };

  const handleUpdatedDocument = (Meta: any, Res: any, metaData: any) => {
    if (versionNumber === 'subartifact') {
      const subartifactDocData: any = {
        file_master_id: Res.id,
        document_data: {
          tmf_artifact_number: metaData.tmf_artifact_number,
          tmf_document_name: metaData.tmf_document_name,
          tmf_doc_type: metaData.tmf_doc_type,
          tmf_doc_subtype: metaData.tmf_doc_subtype,
          tmf_doc_status: 'Uploaded',
          sent_to_isf: false,
          tmf_doc_country:
            Meta.document_data.tmf_doc_level === 'Country' || Meta.document_data.tmf_doc_level === 'Site'
              ? Meta.document_data.tmf_doc_country
              : '',
          tmf_doc_study: metaData.tmf_doc_study,
          tmf_doc_site: Meta.document_data.tmf_doc_level === 'Site' ? Meta.document_data.tmf_doc_site : '',
          tmf_doc_viewer_language: 'English',
          tmf_doc_viewer_approval_cycle: 0,
          tmf_doc_viewer_product_detail: 'test1',
          tmf_doc_viewer_ref_model_version: 'TMF RM 3.2.1',
          tmf_doc_viewer_format: Res.tmf_doc_type,
          tmf_doc_viewer_inspection_ready: '',
          tmf_doc_viewer_ir_issue_type: '',
          tmf_doc_level: metaData.tmf_doc_level,
          tmf_artifact_name: metaData.tmf_artifact_name,
          tmf_doc_viewer_req_certified_copy: 'N',
          tmf_doc_viewer_department: 'None',
          tmf_doc_version_number: metaData.tmf_doc_version_number,
          tmf_doc_milestone: metaData.tmf_doc_milestone,
          tmf_doc_created_by: user,
          tmf_document_id: 'str'
        }
      };
      addSubartifactData(subartifactDocData);
      handleCloseVersion();
    } else if (versionNumber === 'Major' || versionNumber === 'Minor' || versionNumber === 'Overwrite') {
      getAllSubartifacts();
    }
    setIsChanged(false);
  };

  const putUpdatedData = async (docData: any) => {
    dispatch(showLoader());
    try {
      const options = {
        endPointEnv: 'etmf',
        data: docData,
        pathVariables: {
          appName: process.env.REACT_APP_NAME,
          module_section_related: auditmodule,
          parent_entity_related: ''
        }
      };
      const response: any = await sendRequest('update_master_document', options);
      setOpenSubartifact(false);
      if (ipReleaseData && createIPRelease) {
        const isNewDocExistsInIPReleaseData = ipReleaseData.some(
          (releaseDoc: any) => releaseDoc.ip_management_number === response.document_data.tmf_artifact_number
        );
        if (!isNewDocExistsInIPReleaseData) {
          const mockObj = {
            artifact_tooltip: '',
            ip_management_document: response.document_data.tmf_artifact_name,
            ip_management_number: response.document_data.tmf_artifact_number,
            ip_management_comment: ''
          };
          const modifiedObj = [];
          modifiedObj.push(mockObj);
          createIPRelease(modifiedObj);
          setConfirmExitValue(dispatch, true);
        }
      } else if (createDrugSupplyDoc) {
        await createDrugSupplyDocument(response?.document_data.id, response?.document_data.tmf_document_id);
      } else {
        getUploadedDocuments(artifactName);
      }
      handleCloseVersion();
      store.dispatch(
        dispatchApp(
          setMessage({
            msgDetails: `Document - ${docData.document_data.tmf_artifact_name} with version -  ${(
              Math.round(docData.document_data.tmf_doc_version_number * 100) / 100
            ).toFixed(1)} is successfully uploaded`,
            severity: 'success'
          })
        )
      );
    } catch (error) {
      dispatchApp(setMessage({ msgDetails: ipmFileuploadErrorMsg, severity: 'error' }));
    }
    dispatch(hideLoader());
  };
  const scrollType: DialogProps['scroll'] = 'paper';
  return (
    <div className="quallityControl-modal">
      <DialogContent dividers className="quallityControl-modalcontent version-control-modalcontent">
        <Loader />
        <h3> Update Artifact</h3>
        <Grid container>
          <Grid md={12}>
            <p>
              {`${selectData.document_data.tmf_artifact_name}`} artifact already exists. How would you like to make
              updates?
            </p>
          </Grid>

          <Grid item md={12}>
            <FormControl className="radio-section">
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                name="radio-buttons-group"
                defaultValue={versionNumber}
                onChange={handleVersionChange}
              >
                <FormControlLabel
                  value={'Major'}
                  control={<Radio />}
                  label={`Update as major change (ex. Version 1.0 → 2.0)`}
                />
                <FormControlLabel
                  value={'Minor'}
                  control={<Radio />}
                  label={`Update as minor change (ex. Version 1.0 → 1.1)`}
                />
                <FormControlLabel value={'Overwrite'} control={<Radio />} label="Overwrite current version" />
                <FormControlLabel value={'subartifact'} control={<Radio />} label="Upload as a new subartifact" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item md={12} className="button-actions">
            <Grid container spacing={2}>
              <Grid item md={12} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={0}>
                  <Button className="grey-btn" onClick={handleCloseVersion}>
                    Close
                  </Button>
                  <Button onClick={() => handleUpdatedDocument(selectData, resData, metaData)} disabled={!isChanged}>
                    Save
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <PopupComponent child open={openSubartifact} scroll={scrollType}>
        <SelectSubartifact
          versionNumber={versionNumber}
          setOpenSubartifact={setOpenSubartifact}
          setIsChanged={setIsChanged}
          isChanged={isChanged}
          subartifactVersions={subartifactVersions}
          setSubartifactVersions={setSubartifactVersions}
          handleCloseVersion={handleCloseVersion}
          handleBackVersion={handleBackVersion}
          setSubmitDisable={setSubmitDisable}
          overwriteVersionData={overwriteVersionData}
          putUpdatedData={putUpdatedData}
          resData={resData}
          metaData={metaData}
        />
      </PopupComponent>
    </div>
  );
}
